import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Zap, Loader2, Mail, Lock, ChevronRight, AlertTriangle } from 'lucide-react';

const REFERRAL_STORAGE_KEY = 'magnidrop-pending-ref';

/**
 * Supabase Auth login/signup panel.
 *
 * Three sign-in modes toggled by tabs:
 *   1. Email + password (default)
 *   2. Magic link (passwordless)
 *   3. Google OAuth (Supabase provider)
 *
 * `?ref=CODE` from the URL is stashed to sessionStorage so it survives the
 * OAuth round-trip; AuthContext replays it against /api/beta/referral/capture
 * once the Supabase profile is provisioned.
 */
export default function Login() {
    const nav = useNavigate();
    const cfg = useAppConfig();
    const [params] = useSearchParams();
    const {
        user, initializing, supabaseReady,
        loginWithPassword, signUpWithPassword, sendMagicLink, loginWithGoogle,
    } = useAuth();

    const [mode, setMode] = useState('signin'); // signin | signup | magic
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [busy, setBusy] = useState(false);
    const [magicSent, setMagicSent] = useState(false);

    // Capture ?ref=... into sessionStorage so it survives redirects.
    useEffect(() => {
        const ref = params.get('ref');
        if (ref) sessionStorage.setItem(REFERRAL_STORAGE_KEY, ref);
    }, [params]);

    useEffect(() => {
        if (!initializing && user) nav('/dashboard', { replace: true });
    }, [initializing, user, nav]);

    const currentRef = () => {
        try { return sessionStorage.getItem(REFERRAL_STORAGE_KEY) || ''; } catch (_) { return ''; }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!supabaseReady) {
            toast.error('Supabase authentication is not configured yet.');
            return;
        }
        setBusy(true);
        try {
            if (mode === 'signin') {
                await loginWithPassword({ email, password });
                toast.success('Welcome back');
                nav('/dashboard', { replace: true });
            } else if (mode === 'signup') {
                await signUpWithPassword({ email, password, fullName, referral: currentRef() });
                toast.success('Account created — check your inbox to confirm.');
            } else if (mode === 'magic') {
                await sendMagicLink({ email, referral: currentRef() });
                setMagicSent(true);
            }
        } catch (err) {
            toast.error(err?.message || 'Sign-in failed');
        } finally {
            setBusy(false);
        }
    };

    const handleGoogle = async () => {
        if (!supabaseReady) {
            toast.error('Supabase authentication is not configured yet.');
            return;
        }
        setBusy(true);
        try {
            await loginWithGoogle({ referral: currentRef() });
            // Redirects away — no dashboard nav here.
        } catch (err) {
            toast.error(err?.message || 'Google sign-in failed');
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-73px)] grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border bg-card">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 grid place-items-center bg-[#FF6B35] text-white rounded-md">
                        <Zap className="h-4 w-4" strokeWidth={2.5} />
                    </div>
                    <span className="font-heading text-xl font-semibold tracking-tight">Magnidrop</span>
                    {cfg.beta_active && (
                        <span
                            data-testid="login-brand-beta-tag"
                            className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35]/80 font-medium ml-0.5"
                        >
                            (Beta)
                        </span>
                    )}
                </div>
                <div className="max-w-md">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">High-speed transit</p>
                    <h1 className="mt-4 font-heading text-3xl lg:text-4xl font-medium tracking-tighter leading-tight">
                        One URL. Any file, up to 5 TB. Delivered at your maximum internet speed.
                    </h1>
                    {cfg.beta_active && (
                        <div className="mt-8 border border-[#FF6B35]/40 bg-[#FF6B35]/5 p-5">
                            <p className="text-[10px] uppercase tracking-[0.28em] text-[#FF6B35]">Founding tester</p>
                            <p className="mt-3 leading-relaxed text-sm">
                                Sign up today to join the flat <strong>${cfg.beta_flat_price_usd}</strong> Paid Beta —
                                and lock in <strong>50% off</strong> your first month of any commercial tier when we launch.
                            </p>
                        </div>
                    )}
                </div>
                <div className="text-xs text-muted-foreground">
                    Auth by Supabase · Payments by Razorpay · Storage on Cloudflare R2
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-sm" data-testid="login-panel">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Welcome</p>
                    <h2 className="mt-3 font-heading text-3xl font-medium tracking-tight">
                        {mode === 'signup' ? 'Create your Magnidrop account' : 'Sign in to Magnidrop'}
                    </h2>

                    {!supabaseReady && (
                        <div
                            data-testid="supabase-config-warning"
                            className="mt-6 border border-amber-500/40 bg-amber-500/10 p-4 flex gap-3 items-start text-xs leading-relaxed"
                        >
                            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                            <p>
                                Supabase Auth is not configured yet. Add <code className="font-mono">SUPABASE_URL</code>,
                                <code className="font-mono"> SUPABASE_ANON_KEY</code>,
                                <code className="font-mono"> SUPABASE_SERVICE_ROLE_KEY</code> in <code className="font-mono">backend/.env</code>
                                and <code className="font-mono">REACT_APP_SUPABASE_URL</code> /
                                <code className="font-mono">REACT_APP_SUPABASE_ANON_KEY</code> in <code className="font-mono">frontend/.env</code>,
                                then restart both services.
                            </p>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-3 gap-1 text-xs p-1 bg-card border border-border rounded-lg" data-testid="login-mode-tabs">
                        {[
                            { id: 'signin', label: 'Sign in' },
                            { id: 'signup', label: 'Create account' },
                            { id: 'magic',  label: 'Magic link' },
                        ].map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                data-testid={`login-tab-${t.id}`}
                                onClick={() => { setMode(t.id); setMagicSent(false); }}
                                className={`py-2 uppercase tracking-[0.15em] rounded-md transition-colors ${
                                    mode === t.id
                                        ? 'bg-[#FF6B35] text-white shadow-sm'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {mode === 'magic' && magicSent ? (
                        <div data-testid="magic-link-sent" className="mt-8 border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-5">
                            <Mail className="h-5 w-5 text-[#FF6B35]" />
                            <p className="mt-3 font-heading text-lg">Check your inbox.</p>
                            <p className="mt-2 text-sm text-muted-foreground">
                                We sent a one-tap sign-in link to <strong>{email}</strong>. Click it to enter Magnidrop.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            {mode === 'signup' && (
                                <div className="space-y-2">
                                    <Label htmlFor="signup-name">Full name</Label>
                                    <Input
                                    id="signup-name"
                                    data-testid="signup-name-input"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Ada Lovelace"
                                    autoComplete="name"
                                    className="h-11 rounded-lg"
                                />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="login-email">Email</Label>
                                <div className="relative">
                                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        id="login-email"
                                        data-testid="login-email-input"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-9 h-11 rounded-lg"
                                        placeholder="you@studio.com"
                                    />
                                </div>
                            </div>
                            {mode !== 'magic' && (
                                <div className="space-y-2">
                                    <Label htmlFor="login-password">Password</Label>
                                    <div className="relative">
                                        <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            id="login-password"
                                            data-testid="login-password-input"
                                            type="password"
                                            required
                                            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-9 h-11 rounded-lg"
                                            minLength={6}
                                            placeholder="At least 6 characters"
                                        />
                                    </div>
                                </div>
                            )}
                            <Button
                                type="submit"
                                data-testid="login-submit-btn"
                                disabled={busy || !supabaseReady}
                                className="w-full h-11 bg-[#FF6B35] hover:bg-[#E55C2A] text-white rounded-lg"
                            >
                                {busy ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Working…</>
                                ) : (
                                    <>
                                        {mode === 'signin' && 'Sign in'}
                                        {mode === 'signup' && 'Create account'}
                                        {mode === 'magic'  && 'Email me a link'}
                                        <ChevronRight className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </form>
                    )}

                    <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <div className="h-px flex-1 bg-border" />
                        or
                        <div className="h-px flex-1 bg-border" />
                    </div>

                    <Button
                        data-testid="login-google-btn"
                        onClick={handleGoogle}
                        disabled={busy || !supabaseReady}
                        variant="outline"
                        className="w-full h-11 rounded-lg"
                    >
                        <svg className="h-4 w-4 mr-3" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.5 12.3c0-.9-.1-1.6-.3-2.4h-9.9v4.5h5.7c-.1 1-.7 2.5-2.1 3.5l3.05 2.36c1.93-1.78 3.05-4.4 3.05-7.7z"/>
                            <path fill="#34A853" d="M12.24 22.5c2.75 0 5.06-.9 6.75-2.45l-3.22-2.5c-.87.6-2.03.98-3.53.98-2.7 0-4.98-1.78-5.8-4.23l-3.29 2.46c1.68 3.34 5.13 5.62 9.13 5.62z"/>
                            <path fill="#FBBC05" d="M6.44 14.3c-.22-.65-.34-1.35-.34-2.05s.12-1.4.34-2.05L3.15 7.72C2.42 9.03 2 10.48 2 12.25c0 1.77.42 3.22 1.1 4.63l3.34-2.58z"/>
                            <path fill="#EA4335" d="M12.24 5.97c1.9 0 3.19.82 3.93 1.5l2.87-2.8C17.28 2.98 14.99 2 12.24 2 8.24 2 4.79 4.28 3.11 7.62l3.33 2.58c.83-2.45 3.1-4.23 5.8-4.23z"/>
                        </svg>
                        Continue with Google
                    </Button>

                    <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
                        By continuing you agree to Magnidrop&apos;s terms. Session is issued by Supabase GoTrue.
                    </p>
                </div>
            </div>
        </div>
    );
}
